import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Users, Package, BookOpen, Layers, Plus, Sparkles,
  Target, Megaphone, FileText, Lightbulb, ChevronDown, ChevronRight,
  Zap, Eye, Heart, Star, Shield, ShoppingCart, ExternalLink, X, Download,
  Pencil,
} from "lucide-react";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getQueryFn } from "@/lib/queryClient";
import { optimizedImg } from "@/lib/img";
import { useToast } from "@/hooks/use-toast";
import type {
  Campaign, Persona, Product, Narrative, FunnelPlan,
  PersuasionLine, MediaMixEntry, Brief, CreativeOutput, LearningEntry,
} from "@shared/schema";
import { displayUrl, normalizeExampleImage } from "@shared/adImage";

interface CampaignWithRelations {
  id: string;
  brandId: string;
  name: string;
  slug: string;
  personaId: string | null;
  productId: string | null;
  narrativeId: string | null;
  objective: string | null;
  status: string;
  priority: string | null;
  owner: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string | null;
  archivedAt: string | null;
  persona: Persona | null;
  product: Product | null;
  narrative: Narrative | null;
}

const FUNNEL_LAYERS = ["top", "mid", "bottom"] as const;
const PERSUASION_STAGES = ["awareness", "knowledge", "liking", "preference", "conviction", "purchase"] as const;

const funnelLayerLabels: Record<string, string> = {
  top: "Top of Funnel",
  mid: "Mid Funnel",
  bottom: "Bottom of Funnel",
};

const funnelLayerIcons: Record<string, typeof Eye> = {
  top: Eye,
  mid: Heart,
  bottom: ShoppingCart,
};

const persuasionStageIcons: Record<string, typeof Zap> = {
  awareness: Eye,
  knowledge: Lightbulb,
  liking: Heart,
  preference: Star,
  conviction: Shield,
  purchase: ShoppingCart,
};

const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  active: "default",
  paused: "outline",
  completed: "secondary",
  archived: "destructive",
};

function OverviewCard({ label, value, icon: Icon }: { label: string; value: string | null | undefined; icon: typeof Users }) {
  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2 flex flex-row items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-foreground/80 leading-relaxed">{value || "Not set"}</p>
      </CardContent>
    </Card>
  );
}

function EntityCard({ title, subtitle, fields, icon: Icon, color, href }: {
  title: string;
  subtitle?: string | null;
  fields: { label: string; value: string | null | undefined }[];
  icon: typeof Users;
  color: string;
  href?: string;
}) {
  const content = (
    <Card className={`border-border/50 ${href ? "cursor-pointer premium-card" : ""}`} data-testid={`card-entity-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-md ${color}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div>
            <CardTitle className="text-sm">{title}</CardTitle>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {fields.filter(f => f.value).map(f => (
          <div key={f.label}>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{f.label}</p>
            <p className="text-xs text-foreground/80 leading-relaxed line-clamp-3">{f.value}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }
  return content;
}

interface WizardFunnelStage {
  stage: string;
  stage_group: string;
  objective: string;
  psychological_task: string;
  messaging_focus: string;
  emotional_job: string;
  proof_focus: string;
  message_style: string;
  what_not_to_say_yet: string;
  transition_to_next_stage: string;
}

function FunnelPlanSection({ campaignNotes, creativeOutputs }: {
  campaignNotes: string | null;
  creativeOutputs?: CreativeOutput[];
}) {
  const stages: WizardFunnelStage[] = (() => {
    if (!campaignNotes) return [];
    try {
      const snapshot = JSON.parse(campaignNotes);
      return snapshot?.funnelStrategy?.stages ?? [];
    } catch {
      return [];
    }
  })();

  const [expandedStages, setExpandedStages] = useState<Set<number>>(() => new Set(stages.length > 0 ? [0] : []));

  const toggleStage = (idx: number) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (stages.length === 0) {
    return (
      <Card className="border-dashed border-border/50">
        <CardContent className="flex flex-col items-center justify-center py-10 text-center gap-2">
          <Layers className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No funnel strategy generated yet</p>
          <p className="text-xs text-muted-foreground/60">Create a new campaign with the wizard to generate a funnel strategy</p>
        </CardContent>
      </Card>
    );
  }

  const groupColors: Record<string, string> = {
    cognitive: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    affective: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    behavioral: "bg-green-500/10 text-green-600 border-green-500/20",
  };

  return (
    <div className="space-y-2">
      {stages.map((stage, idx) => {
        const isExpanded = expandedStages.has(idx);
        const groupKey = (stage.stage_group || "").toLowerCase();
        const badgeColor = groupColors[groupKey] ?? "bg-muted text-muted-foreground border-border";

        return (
          <div
            key={idx}
            className={`rounded-xl border transition-colors ${isExpanded ? "border-primary/20 bg-card" : "border-border/50 hover:border-border"}`}
            data-testid={`funnel-stage-${idx}`}
          >
            <button
              type="button"
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              onClick={() => toggleStage(idx)}
            >
              <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-primary">{idx + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold">{stage.stage}</span>
              </div>
              <Badge className={`text-[10px] rounded-full px-2 py-0.5 border ${badgeColor}`}>
                {stage.stage_group}
              </Badge>
              {isExpanded
                ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              }
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 pt-1 ml-10 border-t border-border/30 mt-0">
                <p className="text-xs text-foreground/70 leading-relaxed mb-4 italic">
                  {stage.objective}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  {[
                    { label: "Psychological Task", value: stage.psychological_task },
                    { label: "Messaging Focus", value: stage.messaging_focus },
                    { label: "Emotional Job", value: stage.emotional_job },
                    { label: "Proof Focus", value: stage.proof_focus },
                    { label: "Message Style", value: stage.message_style },
                    { label: "Transition to Next", value: stage.transition_to_next_stage },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label}>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-0.5">{f.label}</p>
                      <p className="text-xs text-foreground/80 leading-relaxed">{f.value}</p>
                    </div>
                  ))}
                </div>
                {stage.what_not_to_say_yet && (
                  <div className="mt-3 px-3 py-2 rounded-lg bg-destructive/[0.04] border border-destructive/10">
                    <p className="text-[10px] font-medium uppercase tracking-wider text-destructive/60 mb-0.5">What Not to Say Yet</p>
                    <p className="text-xs text-destructive/80 leading-relaxed">{stage.what_not_to_say_yet}</p>
                  </div>
                )}
                {(() => {
                  const stageAds = (creativeOutputs || []).filter(
                    o => o.imageUrl && o.funnelLayer?.toLowerCase() === stage.stage.toLowerCase()
                  );
                  if (stageAds.length === 0) return null;
                  return (
                    <div className="mt-4 border-t border-border/20 pt-3">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-2">Creative Outputs ({stageAds.length})</p>
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {stageAds.map(ad => (
                          <div key={ad.id} className="shrink-0 group/thumb relative" data-testid={`thumb-${ad.id}`}>
                            <div className="w-16 h-16 rounded-lg overflow-hidden border border-border/30 bg-muted/30">
                              <img src={optimizedImg(ad.imageUrl!, 128, 70)} alt={ad.title} loading="lazy" className="w-full h-full object-cover" />
                            </div>
                            <div className="absolute inset-0 rounded-lg bg-black/60 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                              <p className="text-[8px] text-white text-center px-1 leading-tight line-clamp-3">{ad.title}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PersuasionLinesSection({ persuasionLines, onAddLine }: {
  persuasionLines: PersuasionLine[] | undefined;
  onAddLine: (layer: string) => void;
}) {
  return (
    <div className="space-y-4">
      {FUNNEL_LAYERS.map(layer => {
        const layerLines = persuasionLines?.filter(pl => pl.funnelLayer === layer) || [];

        return (
          <div key={layer} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{funnelLayerLabels[layer]}</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddLine(layer)}
                data-testid={`button-add-persuasion-line-${layer}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
            {layerLines.length > 0 ? (
              <div className="space-y-2">
                {layerLines.map(line => {
                  const StageIcon = persuasionStageIcons[line.persuasionStage] || Zap;
                  return (
                    <Card key={line.id} className="border-border/50" data-testid={`persuasion-line-${line.id}`}>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-start gap-3">
                          <StageIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary" className="text-[10px]">{line.persuasionStage}</Badge>
                              {line.proofType && <Badge variant="outline" className="text-[10px]">{line.proofType}</Badge>}
                              {line.priority && line.priority > 70 && <Badge variant="default" className="text-[10px]">High Priority</Badge>}
                            </div>
                            {line.angle && <p className="text-sm font-medium">{line.angle}</p>}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {line.enemy && (
                                <div>
                                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Enemy</p>
                                  <p className="text-xs text-foreground/80">{line.enemy}</p>
                                </div>
                              )}
                              {line.promise && (
                                <div>
                                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Promise</p>
                                  <p className="text-xs text-foreground/80">{line.promise}</p>
                                </div>
                              )}
                              {line.emotionalTrigger && (
                                <div>
                                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Emotional Trigger</p>
                                  <p className="text-xs text-foreground/80">{line.emotionalTrigger}</p>
                                </div>
                              )}
                              {line.objectionAddressed && (
                                <div>
                                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Objection Addressed</p>
                                  <p className="text-xs text-foreground/80">{line.objectionAddressed}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2 text-center">No persuasion lines for this layer</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MediaMixSection({ mediaMix, persuasionLines, onAddEntry }: {
  mediaMix: MediaMixEntry[] | undefined;
  persuasionLines: PersuasionLine[] | undefined;
  onAddEntry: (layer: string) => void;
}) {
  const plMap = new Map(persuasionLines?.map(pl => [pl.id, pl]) || []);

  return (
    <div className="space-y-4">
      {FUNNEL_LAYERS.map(layer => {
        const layerEntries = mediaMix?.filter(m => m.funnelLayer === layer) || [];

        return (
          <div key={layer} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{funnelLayerLabels[layer]}</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddEntry(layer)}
                data-testid={`button-add-media-mix-${layer}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
            {layerEntries.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {layerEntries.map(entry => {
                  const linkedLine = entry.persuasionLineId ? plMap.get(entry.persuasionLineId) : null;
                  return (
                    <Card key={entry.id} className="border-border/50" data-testid={`media-mix-${entry.id}`}>
                      <CardContent className="py-3 px-4 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          {entry.platform && <Badge variant="default" className="text-[10px]">{entry.platform}</Badge>}
                          {entry.adUnitType && <Badge variant="secondary" className="text-[10px]">{entry.adUnitType}</Badge>}
                          {entry.contentFormat && <Badge variant="outline" className="text-[10px]">{entry.contentFormat}</Badge>}
                        </div>
                        {entry.durationSeconds && (
                          <p className="text-xs text-muted-foreground">{entry.durationSeconds}s duration</p>
                        )}
                        {linkedLine && (
                          <p className="text-[10px] text-muted-foreground">Linked: {linkedLine.angle || linkedLine.persuasionStage}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2 text-center">No media mix entries for this layer</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BriefsSection({ briefs, brandId, campaignId }: {
  briefs: Brief[] | undefined;
  brandId: string;
  campaignId: string;
}) {
  if (!briefs || briefs.length === 0) {
    return (
      <div className="text-center py-8">
        <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No briefs generated yet</p>
        <p className="text-xs text-muted-foreground mt-1">Use the builder to generate briefs from your funnel plan</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {briefs.map(brief => (
        <Card key={brief.id} className="border-border/50" data-testid={`brief-${brief.id}`}>
          <CardContent className="py-3 px-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{brief.title}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant={statusVariants[brief.status] || "secondary"} className="text-[10px]">{brief.status}</Badge>
                  {brief.funnelLayer && <Badge variant="outline" className="text-[10px]">{brief.funnelLayer}</Badge>}
                  {brief.persuasionStage && <Badge variant="outline" className="text-[10px]">{brief.persuasionStage}</Badge>}
                  {brief.cta && <Badge variant="secondary" className="text-[10px]">CTA: {brief.cta}</Badge>}
                </div>
                {brief.briefText && (
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{brief.briefText}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function OutputsSection({ outputs, campaignId }: { outputs: CreativeOutput[] | undefined; campaignId: string }) {
  const campaignOutputs = outputs || [];
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (campaignOutputs.length === 0) {
    return (
      <div className="text-center py-8">
        <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No creative outputs yet</p>
      </div>
    );
  }

  const grouped = campaignOutputs.reduce<Record<string, CreativeOutput[]>>((acc, output) => {
    const stage = output.funnelLayer || "General";
    if (!acc[stage]) acc[stage] = [];
    acc[stage].push(output);
    return acc;
  }, {});

  const withImages = campaignOutputs.filter((o) => o.imageUrl);

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/creatives/download`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "campaign_assets.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      console.error("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadSingle = async (imageUrl: string, name: string) => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/[^a-z0-9]/gi, "_")}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      console.error("Single download failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" />
            <span><strong className="text-foreground">{campaignOutputs.length}</strong> total</span>
            <span className="text-border">|</span>
            <span><strong className="text-foreground">{Object.keys(grouped).length}</strong> stages</span>
          </div>
        </div>
        {withImages.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full text-xs h-8"
            onClick={handleDownloadAll}
            disabled={isDownloading}
            data-testid="btn-download-all"
          >
            {isDownloading ? (
              <Layers className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Package className="w-3.5 h-3.5 mr-1.5" />
            )}
            {isDownloading ? "Preparing..." : `Download All (${withImages.length})`}
          </Button>
        )}
      </div>

      {Object.entries(grouped).map(([stage, items]) => (
        <Card key={stage} className="border-border/40 overflow-hidden" data-testid={`output-stage-${stage}`}>
          <div className="px-4 py-3 bg-muted/30 border-b border-border/30 flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-0.5">{stage}</Badge>
            <span className="text-xs text-muted-foreground">{items.length} creative{items.length !== 1 ? "s" : ""}</span>
          </div>
          <CardContent className="p-3">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {items.map((output) => (
                <div key={output.id} className="group rounded-lg border border-border/30 overflow-hidden bg-card hover:border-border/60 transition-colors" data-testid={`output-${output.id}`}>
                  {output.imageUrl ? (
                    <div
                      className="relative aspect-square bg-muted cursor-pointer overflow-hidden"
                      onClick={() => setLightboxUrl(output.imageUrl)}
                    >
                      <img
                        src={optimizedImg(output.imageUrl, 400, 75)}
                        alt={output.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-1.5 right-1.5">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="rounded-full h-6 text-[9px] px-2 shadow-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadSingle(output.imageUrl!, output.title);
                            }}
                            data-testid={`btn-download-${output.id}`}
                          >
                            <Download className="w-2.5 h-2.5 mr-0.5" />
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-square bg-muted/50 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                  )}
                  <div className="px-2 py-1.5">
                    <p className="text-[10px] font-medium truncate leading-snug">{output.adUnitName || output.title}</p>
                    <Badge variant="secondary" className="text-[8px] px-1 py-0 rounded-full mt-1">{output.assetType || "static"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!lightboxUrl} onOpenChange={() => setLightboxUrl(null)}>
        <DialogContent className="max-w-2xl p-2 bg-black/95 border-white/10 [&>button]:text-white [&>button]:hover:bg-white/20">
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="Full size preview"
              className="w-full max-h-[75vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LearningsSection({ learnings }: { learnings: LearningEntry[] | undefined }) {
  const items = learnings || [];

  if (items.length === 0) {
    return (
      <div className="text-center py-8">
        <Lightbulb className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No learnings recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(entry => (
        <Card key={entry.id} className="border-border/50" data-testid={`learning-${entry.id}`}>
          <CardContent className="py-3 px-4">
            <p className="text-sm font-medium">{entry.title}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {entry.insightType && <Badge variant="secondary" className="text-[10px]">{entry.insightType}</Badge>}
              <Badge variant={statusVariants[entry.status] || "secondary"} className="text-[10px]">{entry.status}</Badge>
              {entry.funnelLayer && <Badge variant="outline" className="text-[10px]">{entry.funnelLayer}</Badge>}
            </div>
            {entry.summary && (
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{entry.summary}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AddFunnelPlanDialog({ open, onOpenChange, layer, campaignId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layer: string;
  campaignId: string;
}) {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/campaigns/${campaignId}/funnel-plans`, {
        funnelLayer: layer,
        ...values,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/funnel-plans`] });
      onOpenChange(false);
      setValues({});
      toast({ title: "Funnel plan added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="tracking-tight">Add {funnelLayerLabels[layer]} Plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stage Goal *</Label>
            <Textarea
              data-testid="input-stage-goal"
              value={values.stageGoal || ""}
              onChange={e => setValues(p => ({ ...p, stageGoal: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Primary Message *</Label>
            <Textarea
              data-testid="input-primary-message"
              value={values.primaryMessage || ""}
              onChange={e => setValues(p => ({ ...p, primaryMessage: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Secondary Message</Label>
            <Textarea
              data-testid="input-secondary-message"
              value={values.secondaryMessage || ""}
              onChange={e => setValues(p => ({ ...p, secondaryMessage: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Key Objection</Label>
            <Input
              data-testid="input-key-objection"
              value={values.keyObjection || ""}
              onChange={e => setValues(p => ({ ...p, keyObjection: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Desired Action</Label>
            <Input
              data-testid="input-desired-action"
              value={values.desiredAction || ""}
              onChange={e => setValues(p => ({ ...p, desiredAction: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!values.stageGoal?.trim() || !values.primaryMessage?.trim() || mutation.isPending}
            className="rounded-full"
            data-testid="button-submit-funnel-plan"
          >
            {mutation.isPending ? "Saving..." : "Add Plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPersuasionLineDialog({ open, onOpenChange, layer, campaignId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layer: string;
  campaignId: string;
}) {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/campaigns/${campaignId}/persuasion-lines`, {
        funnelLayer: layer,
        persuasionStage: values.persuasionStage || "awareness",
        ...values,
        priority: values.priority ? parseInt(values.priority) : 50,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/persuasion-lines`] });
      onOpenChange(false);
      setValues({});
      toast({ title: "Persuasion line added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="tracking-tight">Add Persuasion Line - {funnelLayerLabels[layer]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Persuasion Stage *</Label>
            <Select value={values.persuasionStage || ""} onValueChange={v => setValues(p => ({ ...p, persuasionStage: v }))}>
              <SelectTrigger data-testid="select-persuasion-stage">
                <SelectValue placeholder="Select stage..." />
              </SelectTrigger>
              <SelectContent>
                {PERSUASION_STAGES.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Angle *</Label>
            <Input
              data-testid="input-angle"
              value={values.angle || ""}
              onChange={e => setValues(p => ({ ...p, angle: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Enemy</Label>
            <Input
              data-testid="input-enemy"
              value={values.enemy || ""}
              onChange={e => setValues(p => ({ ...p, enemy: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Promise</Label>
            <Input
              data-testid="input-promise"
              value={values.promise || ""}
              onChange={e => setValues(p => ({ ...p, promise: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Proof Type</Label>
            <Input
              data-testid="input-proof-type"
              value={values.proofType || ""}
              onChange={e => setValues(p => ({ ...p, proofType: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Emotional Trigger</Label>
            <Input
              data-testid="input-emotional-trigger"
              value={values.emotionalTrigger || ""}
              onChange={e => setValues(p => ({ ...p, emotionalTrigger: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Objection Addressed</Label>
            <Input
              data-testid="input-objection-addressed"
              value={values.objectionAddressed || ""}
              onChange={e => setValues(p => ({ ...p, objectionAddressed: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!values.persuasionStage?.trim() || !values.angle?.trim() || mutation.isPending}
            className="rounded-full"
            data-testid="button-submit-persuasion-line"
          >
            {mutation.isPending ? "Saving..." : "Add Line"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMediaMixDialog({ open, onOpenChange, layer, campaignId, persuasionLines }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layer: string;
  campaignId: string;
  persuasionLines: PersuasionLine[] | undefined;
}) {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});

  const layerLines = persuasionLines?.filter(pl => pl.funnelLayer === layer) || [];

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/campaigns/${campaignId}/media-mix`, {
        funnelLayer: layer,
        ...values,
        durationSeconds: values.durationSeconds ? parseInt(values.durationSeconds) : undefined,
        priority: values.priority ? parseInt(values.priority) : 50,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/media-mix`] });
      onOpenChange(false);
      setValues({});
      toast({ title: "Media mix entry added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="tracking-tight">Add Media Mix Entry - {funnelLayerLabels[layer]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Platform *</Label>
            <Select value={values.platform || ""} onValueChange={v => setValues(p => ({ ...p, platform: v }))}>
              <SelectTrigger data-testid="select-platform">
                <SelectValue placeholder="Select platform..." />
              </SelectTrigger>
              <SelectContent>
                {["Meta", "TikTok", "YouTube", "Google", "Pinterest", "Snapchat", "X", "LinkedIn", "Other"].map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Ad Unit Type</Label>
            <Input
              data-testid="input-ad-unit-type"
              value={values.adUnitType || ""}
              onChange={e => setValues(p => ({ ...p, adUnitType: e.target.value }))}
              placeholder="e.g. Story, Reel, In-Feed"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Content Format</Label>
            <Select value={values.contentFormat || ""} onValueChange={v => setValues(p => ({ ...p, contentFormat: v }))}>
              <SelectTrigger data-testid="select-content-format">
                <SelectValue placeholder="Select format..." />
              </SelectTrigger>
              <SelectContent>
                {["Video UGC", "Video Studio", "Static Image", "Carousel", "GIF", "Text Only", "Other"].map(f => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Duration (seconds)</Label>
            <Input
              data-testid="input-duration-seconds"
              type="number"
              value={values.durationSeconds || ""}
              onChange={e => setValues(p => ({ ...p, durationSeconds: e.target.value }))}
            />
          </div>
          {layerLines.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Linked Persuasion Line</Label>
              <SearchableSelect
                value={values.persuasionLineId || ""}
                onValueChange={v => setValues(p => ({ ...p, persuasionLineId: v }))}
                placeholder="Link to a persuasion line..."
                testId="select-persuasion-line"
                options={layerLines.map(pl => ({ value: pl.id, label: pl.angle || pl.persuasionStage }))}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!values.platform?.trim() || mutation.isPending}
            className="rounded-full"
            data-testid="button-submit-media-mix"
          >
            {mutation.isPending ? "Saving..." : "Add Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateBriefDialog({ open, onOpenChange, campaign, funnelPlans, persuasionLines }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: CampaignWithRelations;
  funnelPlans: FunnelPlan[] | undefined;
  persuasionLines: PersuasionLine[] | undefined;
}) {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});

  const availableLayers = Array.from(new Set(funnelPlans?.map(fp => fp.funnelLayer) || []));
  const layerLines = values.funnelLayer
    ? persuasionLines?.filter(pl => pl.funnelLayer === values.funnelLayer) || []
    : [];

  const mutation = useMutation({
    mutationFn: async () => {
      const selectedLine = values.persuasionLineId
        ? persuasionLines?.find(pl => pl.id === values.persuasionLineId)
        : null;

      const res = await apiRequest("POST", "/api/ai/jobs", {
        brandId: campaign.brandId,
        campaignId: campaign.id,
        jobType: "strategy_brief",
        funnelLayer: values.funnelLayer,
        persuasionStage: selectedLine?.persuasionStage || (values.funnelLayer === "top" ? "awareness" : values.funnelLayer === "bottom" ? "purchase" : "knowledge"),
      });
      const result = await res.json();

      await apiRequest("POST", `/api/brands/${campaign.brandId}/briefs`, {
        title: result.output?.title || `Brief: ${campaign.name} - ${funnelLayerLabels[values.funnelLayer]}`,
        campaignId: campaign.id,
        personaId: campaign.personaId,
        productId: campaign.productId,
        narrativeId: campaign.narrativeId,
        funnelLayer: values.funnelLayer,
        persuasionStage: selectedLine?.persuasionStage || "awareness",
        persuasionLineId: values.persuasionLineId || undefined,
        briefText: result.output?.contentMarkdown || "Generated brief content",
        briefMarkdown: result.output?.contentMarkdown,
        status: "draft",
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaign.id}/briefs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${campaign.brandId}/outputs`] });
      onOpenChange(false);
      setValues({});
      toast({ title: "Brief generated successfully" });
    },
    onError: (e: Error) => toast({ title: "Error generating brief", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="tracking-tight flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Generate Brief
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-muted/50 rounded-md space-y-1">
            <p className="text-xs text-muted-foreground">Campaign: <span className="text-foreground font-medium">{campaign.name}</span></p>
            {campaign.persona && <p className="text-xs text-muted-foreground">Persona: <span className="text-foreground font-medium">{campaign.persona.name}</span></p>}
            {campaign.product && <p className="text-xs text-muted-foreground">Product: <span className="text-foreground font-medium">{campaign.product.name}</span></p>}
            {campaign.narrative && <p className="text-xs text-muted-foreground">Narrative: <span className="text-foreground font-medium">{campaign.narrative.name}</span></p>}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Funnel Layer *</Label>
            <Select value={values.funnelLayer || ""} onValueChange={v => setValues(p => ({ ...p, funnelLayer: v, persuasionLineId: "" }))}>
              <SelectTrigger data-testid="select-brief-funnel-layer">
                <SelectValue placeholder="Select funnel layer..." />
              </SelectTrigger>
              <SelectContent>
                {availableLayers.length > 0
                  ? availableLayers.map(l => (
                      <SelectItem key={l} value={l}>{funnelLayerLabels[l]}</SelectItem>
                    ))
                  : FUNNEL_LAYERS.map(l => (
                      <SelectItem key={l} value={l}>{funnelLayerLabels[l]}</SelectItem>
                    ))
                }
              </SelectContent>
            </Select>
          </div>

          {layerLines.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Persuasion Line (optional)</Label>
              <SearchableSelect
                value={values.persuasionLineId || ""}
                onValueChange={v => setValues(p => ({ ...p, persuasionLineId: v }))}
                placeholder="Select a persuasion line..."
                testId="select-brief-persuasion-line"
                options={layerLines.map(pl => ({ value: pl.id, label: pl.angle || pl.persuasionStage }))}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!values.funnelLayer || mutation.isPending}
            className="rounded-full"
            data-testid="button-generate-brief"
          >
            {mutation.isPending ? (
              <>
                <Sparkles className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Generate Brief
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignDetailPage() {
  const { brandId, campaignId } = useParams<{ brandId: string; campaignId: string }>();
  const [funnelPlanDialog, setFunnelPlanDialog] = useState<{ open: boolean; layer: string }>({ open: false, layer: "top" });
  const [persuasionLineDialog, setPersuasionLineDialog] = useState<{ open: boolean; layer: string }>({ open: false, layer: "top" });
  const [mediaMixDialog, setMediaMixDialog] = useState<{ open: boolean; layer: string }>({ open: false, layer: "top" });
  const [briefDialog, setBriefDialog] = useState(false);

  const { data: campaign, isLoading } = useQuery<CampaignWithRelations>({
    queryKey: ["/api/campaigns", campaignId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!campaignId,
  });

  const { data: funnelPlans } = useQuery<FunnelPlan[]>({
    queryKey: [`/api/campaigns/${campaignId}/funnel-plans`],
    enabled: !!campaignId,
  });

  const { data: persuasionLines } = useQuery<PersuasionLine[]>({
    queryKey: [`/api/campaigns/${campaignId}/persuasion-lines`],
    enabled: !!campaignId,
  });

  const { data: mediaMix } = useQuery<MediaMixEntry[]>({
    queryKey: [`/api/campaigns/${campaignId}/media-mix`],
    enabled: !!campaignId,
  });

  const { data: briefs } = useQuery<Brief[]>({
    queryKey: [`/api/campaigns/${campaignId}/briefs`],
    enabled: !!campaignId,
  });

  const { data: outputs } = useQuery<CreativeOutput[]>({
    queryKey: ["/api/campaigns", campaignId, "creatives"],
    queryFn: async () => {
      const res = await fetch(`/api/campaigns/${campaignId}/creatives`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!campaignId,
  });

  const { data: learnings } = useQuery<LearningEntry[]>({
    queryKey: [`/api/brands/${brandId}/learnings`],
    enabled: !!brandId,
  });

  const { data: reviewStats } = useQuery<Record<string, { count: number; avgRating: number | null }>>({
    queryKey: [`/api/brands/${brandId}/product-review-stats`],
    enabled: !!brandId,
  });

  const campaignOutputs = outputs;
  const campaignLearnings = learnings?.filter(l => l.campaignId === campaignId);

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <p className="text-muted-foreground" data-testid="text-not-found">Campaign not found</p>
      </div>
    );
  }

  const snapshot: any = (() => {
    try { return campaign.notes ? JSON.parse(campaign.notes as string) : null; } catch { return null; }
  })();
  const narrativeOpp = snapshot?.narrativeOpportunity ?? null;
  const snapshotAngles: any[] = snapshot?.narrativeAngles ?? [];
  const snapshotAdUnits: any[] = snapshot?.adUnitSelections ?? [];
  const snapshotStages: any[] = snapshot?.funnelStrategy?.stages ?? [];

  const campaignProduct = campaign.product;
  const campaignPersona = campaign.persona;
  const productMedia = campaignProduct && Array.isArray(campaignProduct.mediaJson) ? (campaignProduct.mediaJson as string[]) : [];
  const heroImage = productMedia[0] ?? null;
  const productStats = campaignProduct ? reviewStats?.[campaignProduct.id] : null;

  const totalOutputs = campaignOutputs?.length ?? 0;

  return (
    <div className="max-w-6xl mx-auto" data-testid="page-campaign-detail">
      <div className="px-8 pt-6 pb-2 space-y-5">
        <div className="flex items-center gap-3 fade-in-up">
          <Link href={`/brands/${brandId}/campaigns`}>
            <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid="button-back-campaigns">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight" data-testid="text-campaign-name">{campaign.name}</h1>
              <Badge variant={statusVariants[campaign.status] || "secondary"} className="text-[10px] rounded-full px-2.5" data-testid="badge-campaign-status">
                {campaign.status}
              </Badge>
              {campaign.priority && (
                <Badge variant={campaign.priority === "high" ? "destructive" : campaign.priority === "low" ? "secondary" : "default"} className="text-[10px] rounded-full px-2.5" data-testid="badge-campaign-priority">
                  {campaign.priority}
                </Badge>
              )}
            </div>
            {campaign.objective && (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="text-campaign-objective">{campaign.objective}</p>
            )}
          </div>
          <Link href={`/brands/${brandId}/campaign-wizard/${campaign.id}`}>
            <Button variant="outline" size="sm" className="gap-2" data-testid="button-edit-campaign">
              <Pencil className="h-3.5 w-3.5" />
              Edit Campaign
            </Button>
          </Link>
        </div>

        <div className="flex flex-wrap lg:flex-nowrap items-stretch gap-3 fade-in-up-delay-1" data-testid="card-campaign-summary">
          {campaignPersona && (
            <Link href={`/brands/${brandId}/personas/${campaignPersona.id}`} className="group flex-1 min-w-0">
              <div className="h-full rounded-xl border border-border/40 bg-card/60 px-4 py-3 hover:border-primary/20 hover:bg-card/80 transition-all" data-testid="summary-persona">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-400 shrink-0">
                    <Users className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground/50">Persona</p>
                    <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{campaignPersona.name}</p>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {narrativeOpp && (
            <Link href={`/brands/${brandId}/narratives`} className="group flex-1 min-w-0">
              <div className="h-full rounded-xl border border-border/40 bg-card/60 px-4 py-3 hover:border-primary/20 hover:bg-card/80 transition-all" data-testid="summary-opportunity">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 shrink-0">
                    <Zap className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground/50">Narrative</p>
                    <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{narrativeOpp.title}</p>
                  </div>
                </div>
                {snapshotAngles.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2" data-testid="summary-angles">
                    {snapshotAngles.slice(0, 3).map((angle: any, i: number) => (
                      <span
                        key={angle.id || i}
                        className="inline-flex items-center rounded-full bg-emerald-500/8 border border-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400"
                      >
                        {angle.angleName || angle.name || `Angle ${i + 1}`}
                      </span>
                    ))}
                    {snapshotAngles.length > 3 && (
                      <span className="text-[10px] text-muted-foreground/50 self-center">+{snapshotAngles.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            </Link>
          )}

          {campaignProduct && (
            <Link href={`/brands/${brandId}/products/${campaignProduct.id}`} className="group shrink-0">
              <div className="h-full rounded-xl border border-border/40 bg-card/60 px-4 py-3 hover:border-primary/20 hover:bg-card/80 transition-all flex items-center gap-3" data-testid="summary-product">
                {heroImage && (
                  <div className="w-12 h-12 rounded-lg bg-muted/30 overflow-hidden flex items-center justify-center border border-border/20 shrink-0">
                    <img src={heroImage} alt={campaignProduct.name} className="w-full h-full object-contain p-0.5" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground/50">Product</p>
                  <p className="text-sm font-semibold group-hover:text-primary transition-colors line-clamp-1">{campaignProduct.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {campaignProduct.priceText && (
                      <span className="text-xs font-bold text-foreground">{campaignProduct.priceText}</span>
                    )}
                    {productStats?.avgRating != null && (
                      <div className="flex items-center gap-0.5">
                        <Star className="h-2.5 w-2.5 text-amber-400 fill-amber-400" />
                        <span className="text-[10px] text-muted-foreground">{productStats.avgRating.toFixed(1)} ({productStats.count})</span>
                      </div>
                    )}
                    {campaignProduct.pdpUrl && (
                      <span
                        role="button"
                        className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline ml-1 cursor-pointer"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(campaignProduct.pdpUrl!, "_blank", "noopener,noreferrer"); }}
                        data-testid="link-pdp"
                      >
                        <ExternalLink className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 fade-in-up-delay-2">
          <div className="rounded-lg bg-card/50 border border-border/30 px-3 py-2 text-center">
            <p className="text-base font-bold text-foreground">{snapshotStages.length}</p>
            <p className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-medium">Funnel Stages</p>
          </div>
          <div className="rounded-lg bg-card/50 border border-border/30 px-3 py-2 text-center">
            <p className="text-base font-bold text-foreground">{snapshotAdUnits.length}</p>
            <p className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-medium">Ad Templates</p>
          </div>
          <div className="rounded-lg bg-card/50 border border-border/30 px-3 py-2 text-center">
            <p className="text-base font-bold text-foreground">{totalOutputs}</p>
            <p className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-medium">Outputs</p>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6">

      <Tabs defaultValue="funnel" className="space-y-5 fade-in-up-delay-2">
        <TabsList className="bg-muted/50 rounded-full p-1" data-testid="tabs-campaign-detail">
          <TabsTrigger value="funnel" className="rounded-full text-xs" data-testid="tab-funnel">
            <Layers className="h-3.5 w-3.5 mr-1" /> Funnel
          </TabsTrigger>
          <TabsTrigger value="angles" className="rounded-full text-xs" data-testid="tab-angles">
            <Target className="h-3.5 w-3.5 mr-1" /> Angles
          </TabsTrigger>
          <TabsTrigger value="media" className="rounded-full text-xs" data-testid="tab-media">
            <Megaphone className="h-3.5 w-3.5 mr-1" /> Media Mix
          </TabsTrigger>
          <TabsTrigger value="outputs" className="rounded-full text-xs" data-testid="tab-outputs">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> Outputs
          </TabsTrigger>
          <TabsTrigger value="learnings" className="rounded-full text-xs" data-testid="tab-learnings">
            <Lightbulb className="h-3.5 w-3.5 mr-1" /> Learnings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="funnel" className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold" data-testid="text-funnel-heading">Funnel Strategy</h3>
              <p className="text-xs text-muted-foreground">Stage-by-stage messaging strategy from campaign wizard</p>
            </div>
          </div>
          <FunnelPlanSection
            campaignNotes={campaign?.notes ?? null}
            creativeOutputs={campaignOutputs}
          />
        </TabsContent>

        <TabsContent value="angles" className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold" data-testid="text-angles-heading">Narrative Angles</h3>
            <p className="text-xs text-muted-foreground">Selected angles from the campaign wizard</p>
          </div>
          {(() => {
            const angles = snapshotAngles;
            if (angles.length === 0) {
              return (
                <Card className="border-dashed border-border/50">
                  <CardContent className="flex flex-col items-center justify-center py-10 text-center gap-2">
                    <Target className="h-8 w-8 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">No narrative angles selected</p>
                    <p className="text-xs text-muted-foreground/60">Angles are selected during campaign creation in the wizard</p>
                  </CardContent>
                </Card>
              );
            }
            return (
              <div className="space-y-3">
                {angles.map((angle: any, i: number) => (
                  <Card key={angle.id || i} className="border-border/40" data-testid={`card-angle-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-emerald-600">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0 space-y-3">
                          <div>
                            <h4 className="text-sm font-semibold">{angle.angleName || angle.name || `Angle ${i + 1}`}</h4>
                            {angle.family && (
                              <Badge variant="secondary" className="text-[10px] mt-1 rounded-full">{angle.family}</Badge>
                            )}
                          </div>
                          {angle.summary && (
                            <p className="text-xs text-foreground/70 leading-relaxed">{angle.summary}</p>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
                            {[
                              { label: "Core Tension", value: angle.coreTension },
                              { label: "Belief Shift", value: angle.beliefShift },
                              { label: "Persona Relevance", value: angle.personaRelevance },
                              { label: "Product Truth", value: angle.productTruth },
                              { label: "Promise", value: angle.promise },
                            ].filter(f => f.value).map(f => (
                              <div key={f.label}>
                                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-0.5">{f.label}</p>
                                <p className="text-xs text-foreground/80 leading-relaxed">{f.value}</p>
                              </div>
                            ))}
                          </div>
                          {angle.resonanceScore != null && (
                            <div className="flex gap-3 pt-1 border-t border-border/20">
                              {[
                                { label: "Resonance", value: angle.resonanceScore },
                                { label: "Proof", value: angle.proofScore },
                                { label: "Distinctiveness", value: angle.distinctivenessScore },
                                { label: "Emotional", value: angle.emotionalScore },
                              ].filter(s => s.value != null).map(s => (
                                <div key={s.label} className="text-center">
                                  <p className="text-sm font-bold text-foreground">{s.value}</p>
                                  <p className="text-[9px] text-muted-foreground/60 uppercase">{s.label}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="media" className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold" data-testid="text-media-heading">Media Mix</h3>
            <p className="text-xs text-muted-foreground">Ad unit templates and creative output counts</p>
          </div>
          {(() => {
            const adUnits = snapshotAdUnits;

            const outputCounts: Record<string, number> = {};
            (campaignOutputs || []).forEach(o => {
              if (o.adUnitName) {
                outputCounts[o.adUnitName] = (outputCounts[o.adUnitName] || 0) + 1;
              }
            });
            const totalOutputs = campaignOutputs?.length ?? 0;

            if (adUnits.length === 0 && totalOutputs === 0) {
              return (
                <Card className="border-dashed border-border/50">
                  <CardContent className="flex flex-col items-center justify-center py-10 text-center gap-2">
                    <Megaphone className="h-8 w-8 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">No ad units selected</p>
                    <p className="text-xs text-muted-foreground/60">Ad unit templates are selected during campaign creation</p>
                  </CardContent>
                </Card>
              );
            }

            const stageGroups: Record<string, any[]> = {};
            adUnits.forEach((sel: any) => {
              const stage = sel.funnelStageId || sel.adUnit?.supported_funnel_stages?.[0] || "Unassigned";
              if (!stageGroups[stage]) stageGroups[stage] = [];
              stageGroups[stage].push(sel);
            });

            return (
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span><strong className="text-foreground">{adUnits.length}</strong> templates selected</span>
                  <span><strong className="text-foreground">{totalOutputs}</strong> creatives generated</span>
                </div>

                {Object.entries(stageGroups).map(([stage, units]) => (
                  <div key={stage}>
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">{stage}</h4>
                      <div className="flex-1 h-px bg-border/30" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {units.map((sel: any, i: number) => {
                        const unit = sel.adUnit || {};
                        const generated = outputCounts[unit.name] || 0;
                        const exImg = displayUrl(normalizeExampleImage(unit.exampleImages?.[0]));
                        return (
                          <div
                            key={unit.id || i}
                            className="rounded-xl border border-border/40 p-3 flex gap-3"
                            data-testid={`media-unit-${i}`}
                          >
                            {exImg && (
                              <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted/30 shrink-0 border border-border/20">
                                <img src={exImg} alt={unit.name} className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold leading-snug line-clamp-2">{unit.name}</p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <Badge variant="secondary" className="text-[9px] rounded-full px-1.5 py-0">
                                  {unit.format || "static"}
                                </Badge>
                                {generated > 0 ? (
                                  <span className="text-[10px] text-emerald-600 font-medium">{generated} generated</span>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground/50">0 generated</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {Object.keys(outputCounts).some(name => !adUnits.some((s: any) => s.adUnit?.name === name)) && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">Additional Outputs</h4>
                      <div className="flex-1 h-px bg-border/30" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(outputCounts)
                        .filter(([name]) => !adUnits.some((s: any) => s.adUnit?.name === name))
                        .map(([name, count]) => (
                          <div key={name} className="rounded-xl border border-border/40 p-3">
                            <p className="text-xs font-semibold leading-snug">{name}</p>
                            <span className="text-[10px] text-emerald-600 font-medium mt-1 block">{count} generated</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>


        <TabsContent value="outputs" className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold" data-testid="text-outputs-heading">Creative Outputs</h3>
            <p className="text-xs text-muted-foreground">AI-generated creative content for this campaign</p>
          </div>
          <OutputsSection outputs={campaignOutputs} campaignId={campaignId!} />
        </TabsContent>

        <TabsContent value="learnings" className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold" data-testid="text-learnings-heading">Learnings</h3>
            <p className="text-xs text-muted-foreground">Insights and learnings from campaign performance</p>
          </div>
          <LearningsSection learnings={campaignLearnings} />
        </TabsContent>
      </Tabs>

      <AddFunnelPlanDialog
        open={funnelPlanDialog.open}
        onOpenChange={(open) => setFunnelPlanDialog(p => ({ ...p, open }))}
        layer={funnelPlanDialog.layer}
        campaignId={campaignId}
      />

      <AddPersuasionLineDialog
        open={persuasionLineDialog.open}
        onOpenChange={(open) => setPersuasionLineDialog(p => ({ ...p, open }))}
        layer={persuasionLineDialog.layer}
        campaignId={campaignId}
      />

      <AddMediaMixDialog
        open={mediaMixDialog.open}
        onOpenChange={(open) => setMediaMixDialog(p => ({ ...p, open }))}
        layer={mediaMixDialog.layer}
        campaignId={campaignId}
        persuasionLines={persuasionLines}
      />

      </div>
    </div>
  );
}
